// New chat alerts: a sound, a desktop notification and a blinking tab title. Each person chooses
// them in My account → Chat alerts; the choices are kept in this browser.
//
// Browsers play sounds only after the page was clicked (or typed in) once since it loaded, so
// unlockSound() runs on the first click and the Inbox shows a hint until then.

import { DEFAULT_TONE, DEFAULT_VOLUME, gainOf, TONES, toneFor, toneOf, volumeOf } from '../shared/alert-tones.js';

const KEY = 'hub_alerts';
const DEFAULTS = { sound: true, desktop: true, repeat: true, volume: DEFAULT_VOLUME, tone: DEFAULT_TONE, ticketTone: 'same' };
let audio = null;

export function alertPrefs() {
  try {
    const prefs = { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || '{}') };
    return { ...prefs, volume: volumeOf(prefs.volume), tone: toneOf(prefs.tone) };
  } catch {
    return { ...DEFAULTS };
  }
}

export function setAlertPref(key, value) {
  const prefs = { ...alertPrefs(), [key]: value };
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {}
  return prefs;
}

/** Call from a click or key press: lets the page play sounds. */
export function unlockSound() {
  try {
    if (!audio) audio = new (window.AudioContext || window.webkitAudioContext)();
    return audio.resume ? audio.resume() : Promise.resolve();
  } catch {
    return Promise.resolve();
  }
}

export function soundReady() {
  return !!audio && audio.state === 'running';
}

/** granted | denied | default | unsupported */
export function desktopPermission() {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

export async function askDesktopPermission() {
  if ('Notification' in window && Notification.permission === 'default') await Notification.requestPermission();
  return desktopPermission();
}

/** Plays a tone at a volume (0-100), whatever the sound setting says: the settings card uses it to let you hear a choice. */
export function playTone(id, volume = alertPrefs().volume) {
  if (!soundReady()) return;
  const tone = TONES[toneOf(id)];
  const t = audio.currentTime + 0.02;
  // Everything goes through one volume and a limiter, so notes that overlap or a high volume never crackle.
  const master = audio.createGain();
  master.gain.value = gainOf(volume);
  const limiter = audio.createDynamicsCompressor();
  limiter.threshold.value = -12;
  limiter.ratio.value = 12;
  master.connect(limiter).connect(audio.destination);
  for (const [freq, at] of tone.notes) {
    for (const [multiple, loudness] of tone.partials) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = tone.wave;
      osc.frequency.value = freq * multiple;
      gain.gain.setValueAtTime(0.0001, t + at);
      gain.gain.exponentialRampToValueAtTime(0.9 * loudness, t + at + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + at + tone.len);
      osc.connect(gain).connect(master);
      osc.start(t + at);
      osc.stop(t + at + tone.len + 0.05);
    }
  }
}

/** The alert sound (when it is on): kind 'ticket' can have a tone of its own. */
export function beep(kind = 'chat') {
  const prefs = alertPrefs();
  if (!prefs.sound) return;
  playTone(toneFor(prefs, kind), prefs.volume);
}

/**
 * Sound, blinking title, and a desktop notification when this window isn't in front (or always
 * with force). onClick runs when the notification is clicked.
 */
export function alert(title, body, { tag = 'hub', sticky = false, force = false, kind = 'chat', onClick } = {}) {
  beep(kind);
  blinkTitle(title);
  const away = document.visibilityState !== 'visible' || !document.hasFocus();
  if (!(away || force) || !alertPrefs().desktop || desktopPermission() !== 'granted') return;
  try {
    const n = new Notification(title, { body: String(body || '').slice(0, 140), tag, renotify: true, requireInteraction: sticky });
    n.onclick = () => {
      focus();
      onClick?.();
      n.close();
    };
  } catch {}
}

// ------------------------------------------------------------------ what's new

/** Remembers the conversations that exist now (and who has them), so only later changes alert. */
export async function seedKnown(known) {
  try {
    const res = await fetch('/api/chat/conversations?view=all&limit=100');
    if (res.ok) for (const c of await res.json()) known.set(c.id, c.assignee);
  } catch {}
}

/**
 * What a conversation update means for me: 'chat' (a live chat is now mine), 'waiting' (a new
 * chat nobody has), 'ticket' (a ticket is now mine), or null. Uses the conversation's own times,
 * not this computer's clock (which may be off).
 */
const aiBefore = new Map(); // conversation id -> its AI state when last seen

export function newFor(known, c, myId) {
  const seen = known.has(c.id);
  const wasAi = aiBefore.get(c.id);
  aiBefore.set(c.id, c.ai || null);
  const before = known.get(c.id);
  known.set(c.id, c.assignee);
  if (c.status === 'spam') return null; // (a ticket marked as spam alerts nobody)
  const fresh = !seen && Math.abs((c.updated_at || 0) - (c.created_at || 0)) < 15000; // created a moment ago
  const becameMine = c.assignee === myId && (seen ? before !== myId : fresh);
  const startedByAgent = c.first_response_at && c.first_response_at === c.created_at; // "Start chat" from the dashboard
  if (c.live && becameMine && !(startedByAgent && !seen)) return 'chat';
  // Chats the AI agent answers don't wait for the team, until it hands them off.
  if (c.live && !c.assignee && fresh && c.ai !== 'active') return 'waiting';
  if (c.live && !c.assignee && c.ai === 'handoff' && wasAi === 'active') return 'waiting';
  if (!c.live && becameMine) return 'ticket';
  return null;
}

// ------------------------------------------------------------------ the tab title

let blinkTimer = null;
let baseTitle = document.title;

/** The title to show when not blinking (e.g. with the number of waiting chats). */
export function setBaseTitle(title) {
  baseTitle = title;
  if (!blinkTimer) document.title = title;
}

function blinkTitle(text) {
  if (document.visibilityState === 'visible' && document.hasFocus()) return;
  stopBlink();
  let on = false;
  blinkTimer = setInterval(() => (document.title = (on = !on) ? `🔔 ${text}` : baseTitle), 1000);
}

function stopBlink() {
  if (!blinkTimer) return;
  clearInterval(blinkTimer);
  blinkTimer = null;
  document.title = baseTitle;
}

addEventListener('focus', stopBlink);
document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && document.hasFocus() && stopBlink());
